/**
 * Presentation Layer - DoctorEditDialog Component
 * 担当医師編集ダイアログコンポーネント
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { DOCTOR_OPTIONS, MANUAL_INPUT_VALUE } from '../../../../domain/medicalReservation/EditableFieldsPolicy';

/**
 * Props定義
 */
export interface DoctorEditDialogProps {
  /** ダイアログが開いているか */
  open: boolean;
  /** 現在の値 */
  currentValue: string;
  /** 保存時のコールバック */
  onSave: (value: string) => void;
  /** キャンセル時のコールバック */
  onCancel: () => void;
}

/**
 * 担当医師編集ダイアログコンポーネント
 */
export const DoctorEditDialog: React.FC<DoctorEditDialogProps> = ({
  open,
  currentValue,
  onSave,
  onCancel,
}) => {
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [isManualInput, setIsManualInput] = useState(false);
  const [manualInputValue, setManualInputValue] = useState('');

  // ダイアログが開いたときに初期値を設定
  useEffect(() => {
    if (open) {
      // 現在の値が選択肢にあるかチェック
      const isExistingOption = DOCTOR_OPTIONS.some(
        (opt) => opt.value === currentValue || opt.label === currentValue
      );

      if (currentValue && !isExistingOption) {
        // 選択肢にない場合は手動入力モード
        setIsManualInput(true);
        setManualInputValue(currentValue);
        setSelectedValue('');
      } else {
        // 選択肢にある場合はそれを選択
        setIsManualInput(false);
        setManualInputValue('');
        setSelectedValue(currentValue);
      }
    }
  }, [open, currentValue]);

  /**
   * 選択変更ハンドラ
   */
  const handleSelectChange = useCallback(
    (event: { target: { value: string } }) => {
      const value = event.target.value;
      if (value === MANUAL_INPUT_VALUE) {
        setIsManualInput(true);
        setSelectedValue('');
      } else {
        setIsManualInput(false);
        setSelectedValue(value);
      }
    },
    []
  );

  /**
   * 手動入力変更ハンドラ
   */
  const handleManualInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setManualInputValue(event.target.value);
    },
    []
  );

  /**
   * 保存ハンドラ
   */
  const handleSave = useCallback(() => {
    const valueToSave = isManualInput ? manualInputValue : selectedValue;
    if (valueToSave.trim()) {
      onSave(valueToSave);
    }
  }, [isManualInput, manualInputValue, selectedValue, onSave]);

  /**
   * キャンセルハンドラ
   */
  const handleCancel = useCallback(() => {
    setSelectedValue('');
    setIsManualInput(false);
    setManualInputValue('');
    onCancel();
  }, [onCancel]);

  const isSaveDisabled = isManualInput
    ? !manualInputValue.trim()
    : !selectedValue;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        担当医師を選択
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* 現在の値 */}
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            現在の値: {currentValue || '未設定'}
          </Typography>

          {/* 選択 or 手動入力 */}
          {!isManualInput ? (
            <FormControl fullWidth size="small">
              <InputLabel id="doctor-select-label">担当医師</InputLabel>
              <Select
                labelId="doctor-select-label"
                value={selectedValue}
                label="担当医師"
                onChange={handleSelectChange}
              >
                {DOCTOR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem value={MANUAL_INPUT_VALUE}>
                  <em>手動入力...</em>
                </MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Box>
              <TextField
                fullWidth
                size="small"
                label="医師名を入力"
                value={manualInputValue}
                onChange={handleManualInputChange}
                autoFocus
                placeholder="例: 田中 太郎"
              />
              <Button
                size="small"
                onClick={() => setIsManualInput(false)}
                sx={{ mt: 1 }}
              >
                選択肢から選ぶ
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>キャンセル</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveDisabled}
        >
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};
